import { Button, Spinner } from '@mm/components';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Drawer } from 'antd';
import { ReactComponent as MagicPencilIcon } from 'assets/icons/magic-pen.svg';
import React, { useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { useSelector } from 'react-redux';
import { ReactComponent as HtmlEditorEmptyState } from '../svg/html-editor-empry-state.svg';
import css from './HtmlEditor.module.css';

import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { useEmailWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';

type Props = {
  stateAndHandlers: ReturnType<typeof useEmailWidgetHandlers>;
};

export const HtmlEditor = ({ stateAndHandlers }: Props) => {
  const { bodyJsonValue, setBodyJsonValue, bodyDdValue, setBodyDdValue } =
    stateAndHandlers;

  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const signature = useSelector(activeFacebookPageSelector)?.unlayerHmac;

  const { retrievePage } = useConnectedPageCRUDActions();

  useEffect(() => {
    if (!signature && facebookPageId) {
      retrievePage(facebookPageId, false, false, false, false, true);
    }
  }, [facebookPageId]);

  const onLoad = () => {
    if (bodyJsonValue) {
      emailEditorRef?.current?.editor?.loadDesign(bodyJsonValue);
    }
  };

  const onReady = () => {
    setShowLoadingIndicator(false);
  };

  const html = bodyDdValue || '';

  return !showHtmlEditor ? (
    <div className={css.container}>
      <div className={css.htmlEditButtonContainer}>
        <Button
          css={{ container: css.editHtmlButton }}
          leftIcon={<MagicPencilIcon />}
          onClick={() => {
            setShowHtmlEditor(true);
            setShowLoadingIndicator(true);
          }}
        >
          Edit Email
        </Button>
      </div>

      {html ? (
        <iframe title="email-preview" srcDoc={html} className={css.container} />
      ) : (
        <div className={css.emptyState}>
          <HtmlEditorEmptyState />
        </div>
      )}
    </div>
  ) : (
    <Drawer
      open={showHtmlEditor}
      width="100%"
      zIndex={2000002}
      onClose={() => {
        setShowHtmlEditor(false);
        setShowLoadingIndicator(false);
      }}
      title={
        <Button
          css={{ container: css.saveButton }}
          onClick={() => {
            if (emailEditorRef.current?.editor) {
              emailEditorRef.current?.editor?.exportHtml(data => {
                const { design, html } = data;

                setBodyJsonValue(design);
                setBodyDdValue(html);
                setShowHtmlEditor(false);
                setShowLoadingIndicator(false);
              });
            } else {
              setShowHtmlEditor(false);
              setShowLoadingIndicator(false);
            }
          }}
        >
          Save
        </Button>
      }
    >
      {showLoadingIndicator && <Spinner show backgroundColor="transparent" />}
      {signature && (
        <EmailEditor
          ref={emailEditorRef}
          onLoad={onLoad}
          onReady={onReady}
          options={{
            id: 'editor',
            projectId: UNLAYER_PROJECT_ID,
            displayMode: 'email',
            user: {
              id: facebookPageId, // Note: we want uploaded images to be available to people who have access to the page
              signature,
            },
            appearance: {
              theme: 'modern_light',
              panels: {
                tools: {
                  dock: 'left',
                },
              },
            },
          }}
          style={{ height: '85vh' }}
        />
      )}
    </Drawer>
  );
};
